import { useState, useEffect } from 'react';
import { FiBell, FiX } from 'react-icons/fi';
import { getPushSupportInfo } from '@/lib/push';
import './PushPermissionPrompt.css';

interface PushPermissionPromptProps {
  onRequest: () => Promise<{ success: boolean; reason?: string }>;
  onDismiss: () => void;
}

/**
 * Componente elegante para solicitar permisos de notificaciones push.
 * Optimizado para móvil con un diseño atractivo.
 */
export function PushPermissionPrompt({ onRequest, onDismiss }: PushPermissionPromptProps) {
  const [isRequesting, setIsRequesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [supportInfo, setSupportInfo] = useState<ReturnType<typeof getPushSupportInfo> | null>(null);

  useEffect(() => {
    setSupportInfo(getPushSupportInfo());
  }, []);

  const handleRequest = async () => {
    setIsRequesting(true);
    setMessage(null);
    
    try {
      const result = await onRequest();
      
      if (result.success) {
        setMessage({ type: 'success', text: '¡Notificaciones activadas! 🎉' });
        setTimeout(() => onDismiss(), 2000);
      } else {
        let errorMessage = 'No se pudieron activar las notificaciones. Inténtalo más tarde.';
        
        if (result.reason === 'denied') {
          errorMessage = 'Permisos denegados. Puedes activarlos desde la configuración del navegador.';
        } else if (result.reason === 'ios_pwa_required') {
          errorMessage = 'En iOS, necesitas instalar la app como PWA. Toca el botón de compartir y selecciona "Agregar a pantalla de inicio".';
        } else if (result.reason === 'unsupported') {
          errorMessage = 'Tu navegador no soporta notificaciones push. Asegúrate de estar usando iOS 16.4+ y haber instalado la app como PWA.';
        } else if (result.reason === 'missing_vapid_key') {
          errorMessage = 'Error de configuración. Contacta al soporte.';
        } else if (result.reason === 'registration_failed') {
          errorMessage = 'No se pudo registrar el service worker. Intenta recargar la página.';
        } else if (result.reason === 'service_worker_not_active') {
          errorMessage = 'El service worker no está activo. Intenta recargar la página.';
        } else if (result.reason === 'subscription_failed') {
          errorMessage = 'No se pudo crear la suscripción. Verifica que la app esté instalada como PWA.';
        } else if (result.reason === 'persist_failed') {
          errorMessage = 'No se pudo guardar la suscripción. Inténtalo más tarde.';
        }
        
        console.error('[PushPermissionPrompt] Error:', result.reason, result);
        setMessage({ type: 'error', text: errorMessage });
      }
    } catch (error) {
      console.error('[PushPermissionPrompt] Excepción:', error);
      setMessage({ type: 'error', text: 'Ocurrió un error al activar las notificaciones. Revisa la consola para más detalles.' });
    } finally {
      setIsRequesting(false);
    }
  };

  // Si no hay soporte de Notification API o requiere PWA en iOS, mostrar mensaje especial
  if (supportInfo && (!supportInfo.hasNotificationAPI || supportInfo.requiresPWA)) {
    return (
      <div className="push-permission-overlay">
        <div className="push-permission-card">
          <button 
            className="push-permission-close"
            onClick={onDismiss}
            aria-label="Cerrar"
          >
            <FiX size={20} />
          </button>
          
          <div className="push-permission-icon">
            <FiBell size={48} />
          </div>
          
          <h2 className="push-permission-title">
            Instala la app para notificaciones
          </h2>
          
          <p className="push-permission-description">
            En iOS, las notificaciones push solo funcionan cuando la app está instalada como PWA (Progressive Web App), tanto en Safari como en Chrome.
          </p>
          
          <div className="push-permission-features">
            <div className="push-permission-feature">
              <span className="push-permission-feature-icon">📱</span>
              <span className="push-permission-feature-text">Toca el botón de compartir (cuadrado con flecha)</span>
            </div>
            <div className="push-permission-feature">
              <span className="push-permission-feature-icon">➕</span>
              <span className="push-permission-feature-text">Selecciona "Agregar a pantalla de inicio"</span>
            </div>
            <div className="push-permission-feature">
              <span className="push-permission-feature-icon">🔔</span>
              <span className="push-permission-feature-text">Luego podrás activar las notificaciones</span>
            </div>
          </div>
          
          <div className="push-permission-actions">
            <button
              className="push-permission-button push-permission-button--secondary"
              onClick={onDismiss}
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="push-permission-overlay">
      <div className="push-permission-card">
        <button 
          className="push-permission-close"
          onClick={onDismiss}
          aria-label="Cerrar"
        >
          <FiX size={20} />
        </button>
        
        <div className="push-permission-icon">
          <FiBell size={48} />
        </div>
        
        <h2 className="push-permission-title">
          No te pierdas nada
        </h2>
        
        <p className="push-permission-description">
          Activa las notificaciones para recibir alertas de nuevos mensajes y conexiones en tiempo real.
        </p>
        
        <div className="push-permission-features">
          <div className="push-permission-feature">
            <span className="push-permission-feature-icon">💬</span>
            <span className="push-permission-feature-text">Mensajes instantáneos</span>
          </div>
          <div className="push-permission-feature">
            <span className="push-permission-feature-icon">👥</span>
            <span className="push-permission-feature-text">Nuevas conexiones</span>
          </div>
          <div className="push-permission-feature">
            <span className="push-permission-feature-icon">📅</span>
            <span className="push-permission-feature-text">Actualizaciones de eventos</span>
          </div>
        </div>

        {message && (
          <div className={`push-permission-message push-permission-message--${message.type}`}>
            {message.text}
          </div>
        )}
        
        <div className="push-permission-actions">
          <button
            className="push-permission-button push-permission-button--primary"
            onClick={handleRequest}
            disabled={isRequesting}
          >
            {isRequesting ? 'Activando...' : 'Activar notificaciones'}
          </button>
          <button
            className="push-permission-button push-permission-button--secondary"
            onClick={onDismiss}
            disabled={isRequesting}
          >
            Ahora no
          </button>
        </div>
        
        <p className="push-permission-privacy">
          Tus datos están seguros. Puedes desactivar las notificaciones cuando quieras.
        </p>
      </div>
    </div>
  );
}

